import styled, { css } from "styled-components";
import { media, rgba, p3 } from "./stylesTheme";
import { borderRadius, getColor } from "./stylesUtils";
import { getCSSImageUrl } from "../utils/mediaUrl";

export const StyledChatBox = styled.div`
    width: 100%;
    margin: 0 auto;
    height: 100svh;
    overflow-y: scroll;
    overscroll-behavior: none;
    font-size: 1.3em;
    display: flex;
    flex-direction: column-reverse;
    overflow: hidden;
    overflow-y: scroll;

    > div {
        width: 100%;
        max-width: 800px;
        margin: 0 auto ${(props) => (props.$hasVideo ? "21em" : "12em")};

        /* Clearfix for motion.div wrappers containing floated messages */
        > div::after {
            content: "";
            display: table;
            clear: both;
        }
    }

    ${media.respond4} {
        > div {
            max-width: 720px;
        }
    }

    ${media.respond35} {
        > div {
            max-width: 680px;
            margin: 0 auto ${(props) => (props.$hasVideo ? "21em" : "12em")};
        }
    }

    ${media.respond3} {
        > div {
            max-width: 570px;
        }
    }

    ${media.respond1} {
        > div {
            max-width: 680px;
        }
    }

    ${media.tablet} {
        > div {
            max-width: 720px;
        }
    }

    ${media.mobile} {
        font-size: 1.4em;
        > div {
            width: calc(100% - 2.4em);
            padding: 0 1.2em;
            margin: 0 auto ${(props) => (props.$hasVideo ? "23em" : "8em")};
        }
    }

    /* Actual touch tablets (not desktop at tablet width) */
    ${media.touchTablet} {
        > div {
            margin: 0 auto ${(props) => (props.$hasVideo ? "32em" : "14em")};
        }
    }
`;

export const BlankPlaceholder = styled.div`
    display: inline-block;
    width: 3em;
    margin: 0 0.1em;
    height: 1.6em;
    background: ${(props) => rgba(props.theme, "black", 0.1)};
    border-radius: 0.2em;
    vertical-align: middle;
    cursor: default;
`;

// ============================================
// BASE MESSAGE STYLES (shared by all messages)
// ============================================
const baseMessageStyles = css`
    position: relative;
    will-change: transform, opacity;
    clear: both;
    font-size: 1.1em;
    line-height: 1.5em;

    /* Disable text selection highlighting */
    &::selection {
        background: transparent;
        color: inherit;
    }

    * {
        &::selection {
            background: transparent;
            color: inherit;
        }
    }

    /* Hover area for audio buttons */
    &:after {
        content: "";
        position: absolute;
        bottom: -1em;
        left: -5%;
        width: 110%;
        height: 1em;
        background: rgba(0, 0, 0, 0);
        cursor: default;
    }

    /* Audio button animations */
    .replay-audio,
    .playback-audio {
        opacity: 0;
        transition: 0.15s;
        transform: translateY(60%);
    }

    .replay-audio.talking,
    .replay-audio.loading,
    .playback-audio.playing,
    .playback-audio.loading {
        opacity: 1;
        transform: translateY(50%);
    }

    &:hover {
        .replay-audio,
        .playback-audio {
            opacity: 1;
            transform: translateY(50%);
        }
    }

    ${media.mobile} {
        .replay-audio,
        .playback-audio {
            opacity: 1;
            transform: translateY(50%);
        }
    }

    /* Roleplay text styling */
    .roleplay-prefix {
        opacity: 0.5;
        display: inline;
    }

    .roleplay-scenario {
        opacity: 1;
        display: inline;
    }

    span.highlighted {
        color: rgba(35, 34, 18, 0.9);
        color: color(display-p3 0.136 0.133 0.0793 / 0.9);
    }

    /* Translatable container gets highlighter cursor */
    &[data-translatable="true"] {
        cursor: url("${getCSSImageUrl("img/highlight.svg")}") 10 43, auto;
    }
`;

// ============================================
// MESSAGE TAIL (speech bubble pointer)
// ============================================
const messageTail = css`
    &:before {
        content: "";
        position: absolute;
        bottom: 0;
        width: 2.6em;
        height: 1.3em;
        mask: url("${getCSSImageUrl("img/cornerLeftMask.svg")}") no-repeat
            center / contain;
        -webkit-mask: url("${getCSSImageUrl("img/cornerLeftMask.svg")}")
            no-repeat center / contain;
        z-index: -1;
        pointer-events: none;
    }
`;

// ============================================
// AI MESSAGE
// ============================================
const aiMessageStyles = css`
    margin: 0 0 2em 0;
    float: left;
    clear: both;
    padding: 0.9em 1.2em;
    max-width: 80%;
    color: ${getColor("colortextAi")};
    cursor: default;
    text-align: left;

    background: ${getColor("colorAI")};
    ${borderRadius(0.5, 0.5, 0.5, 0)};

    ${messageTail}

    &:before {
        left: -1.3em;
        background: ${getColor("colorAI")};
    }
`;

// ============================================
// USER MESSAGE
// ============================================
const userMessageStyles = css`
    float: right;
    margin-bottom: 2em;
    padding: 0.9em 1.2em;
    max-width: 80%;
    color: ${getColor("colortextYou")};
    cursor: default;
    background: ${getColor("colorYou")};
    ${borderRadius(0.5, 0.5, 0, 0.5)};

    ${messageTail}

    &:before {
        right: -1.3em;
        transform: scaleX(-1);
        background: ${(props) => rgba(props.theme, "colorYou", 1)};
        background: ${(props) => p3(props.theme, "pcolorYou", 1)};
    }
`;

// ============================================
// CORRECTION MESSAGE
// ============================================
const correctionMessageStyles = css`
    float: right;
    margin-bottom: 2em;
    padding: 0;
    max-width: 80%;
    color: ${getColor("colortextCorrect")};
    ${borderRadius(0.5, 0.5, 0, 0.5)};

    ${messageTail}

    &:before {
        right: -1.3em;
        transform: scaleX(-1);
        background: ${getColor("colorCorrect")};
    }

    .original,
    .corrected {
        padding: 0.9em 1.2em;
        ${borderRadius(0.5, 0.5, 0, 0.5)};
    }

    .original {
        color: ${getColor("colortextYou", 0.6)};
        background: ${getColor("colorYou", 0.6)};
        border-bottom-right-radius: 0;
        border-bottom-left-radius: 0;
        padding-bottom: 2.2em;
        position: relative;
    }

    .corrected {
        z-index: 1;
        margin-top: -1.5em;
        position: relative;
        background: ${getColor("colorCorrect")};

        u {
            font-family: var(--bold);
            text-decoration: none;
            position: relative;

            &:after {
                content: "";
                position: absolute;
                bottom: -0.15em;
                left: 1%;
                width: 98%;
                height: 0.1em;
                background: ${getColor("colortextCorrect")};
                border-radius: 2em;
            }
        }
    }
`;

// ============================================
// CAPTION MESSAGE
// ============================================
const captionMessageStyles = css`
    margin: 0 0 0.5em 0;
    float: left;
    clear: both;
    max-width: 90%;
    font-size: 0.95em !important;
    text-align: left;
    color: ${getColor("colortextYou")};
    background: ${getColor("colorYou")};
    ${borderRadius(0.5)};
    padding: 1em 1.4em;
    white-space: pre-line;

    &:before {
        display: none;
    }
`;

// ============================================
// BJORN WELCOME MESSAGE
// ============================================
const bjornMessageStyles = css`
    float: none;
    clear: both;
    margin-top: 5em;
    margin-bottom: 2em;
    padding: 0.9em 3.2em;
    max-width: 100%;
    width: calc(100% - 6.4em);
    text-align: center;
    color: ${getColor("black", 0.5)};
    background: ${getColor("black", 0.05)};
    ${borderRadius(0.5)};
    pointer-events: none;

    &:before {
        content: "";
        position: absolute;
        left: 1.1em;
        top: 50%;
        margin-top: -0.45em;
        opacity: 1;
        pointer-events: none;
        width: 0.9em;
        height: 0.9em;
        mask: url("${getCSSImageUrl("img/star.svg")}") no-repeat center /
            contain;
        -webkit-mask: url("${getCSSImageUrl("img/star.svg")}") no-repeat center /
            contain;
        background: ${getColor("black", 0.4)};
        mix-blend-mode: overlay;
        transform-origin: center center;
        will-change: transform;
    }

    ${media.mobile} {
        width: calc(100% - 6.4em);
    }
`;

// ============================================
// LOADER MESSAGE
// ============================================
const loaderMessageStyles = css`
    width: 2em;
    height: 0.5em;
    opacity: 0.7;
    padding: 1.1em 0.8em;
    margin-bottom: 2em;
    ${borderRadius(0.4)};
    font-size: 1em;
`;

const loaderLeftStyles = css`
    background: ${getColor("colorAI", 0.7)};
    ${loaderMessageStyles}
    float: left;
`;

const loaderRightStyles = css`
    background: ${getColor("colorYou", 0.7)};
    ${loaderMessageStyles}
    float: right;
`;

// ============================================
// OPTIONS MESSAGE (clickable options)
// ============================================
const optionsMessageStyles = css`
    float: right;
    padding: 0;
    margin-bottom: 0.8em;
    max-width: 80%;
    opacity: 0.7;
    transition: background-color 150ms, opacity 150ms;
    cursor: pointer;
    color: ${getColor("colortextYou")};
    background: ${getColor("colorYou")};
    ${borderRadius(0.3)};

    &:hover {
        opacity: 0.85;
    }

    &:active {
        background: ${getColor("colorYou", 1, 4)};
    }

    font-size: 84%;
    margin: 0;
    box-sizing: border-box;

    > div {
        padding: 0.6em 0.85em 0.65em;
        box-sizing: border-box;
    }
`;

const optionsSmallStyles = css`
    ${optionsMessageStyles}
    margin-bottom: 2em;
`;

// ============================================
// FULL-WIDTH OPTIONS (language, roleplay, deck, podcast)
// ============================================
const fullWidthOptionsStyles = css`
    max-width: 100%;
    width: 100%;
    padding: 0;
    position: relative;
    margin-bottom: 2em;

    &:before {
        display: none;
    }
`;

// ============================================
// TYPE MODE MESSAGE
// ============================================
const typeModeMessageStyles = css`
    max-width: 110%;
    width: 110%;
    margin-right: -11.3%;
    padding: 1.9em 2.2em;

    ${media.mobile} {
        width: 92%;
        margin-right: -5.5%;
        border-radius: 0;
        max-width: 90%;
        left: -4%;
        font-size: 70%;
        margin-bottom: 0;
        margin-top: 2em;
    }
`;

// ============================================
// MULTIPLE CHOICE MESSAGE
// ============================================
const multipleChoiceMessageStyles = css`
    max-width: 100%;
    padding: 1em;
    float: right;
    background: ${getColor("colorYou")};
    margin-bottom: 2em;

    ${borderRadius(0.5, 0.5, 0, 0.5)};

    ${messageTail}

    &:before {
        right: -1.3em;
        transform: scaleX(-1);
        background: ${getColor("colorYou")};
    }
`;

// ============================================
// CONTEXT SETTER MESSAGE (roleplay intro)
// ============================================
const contextSetterStyles = css`
    max-width: 60%;
    margin-left: auto;
    margin-right: auto;
    float: none;
    text-wrap: pretty;
    font-family: var(--med);
    font-size: 0.9em;
    color: ${getColor("black", 0.7)};
    background: ${getColor("black", 0.1)};
    white-space: pre-line;
    ${borderRadius(0.3)};

    &:before {
        display: none;
    }
`;

// ============================================
// STARTING MESSAGE (loading animation)
// ============================================
const startingMessageStyles = css`
    color: ${getColor("black", 0.5)};
    background: ${getColor("black", 0.1)};

    @keyframes opacityAnimation {
        0%,
        100% {
            opacity: 0.6;
        }
        50% {
            opacity: 0.7;
        }
    }

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }

    animation: opacityAnimation 0.8s infinite;

    &:before {
        animation: spin 0.3s linear infinite;
    }
`;

// ============================================
// SYSTEM MESSAGE (hidden)
// ============================================
const systemMessageStyles = css`
    display: none;
`;

// ============================================
// MAIN STYLED COMPONENT
// ============================================
export const SharedMessageStyles = styled.div`
    ${baseMessageStyles}

    /* Apply specific styles based on $sender prop */
    ${(props) => props.$sender === "ai" && aiMessageStyles}
    ${(props) => props.$sender === "aiHomeLang" && aiMessageStyles}
    ${(props) => props.$sender === "user" && userMessageStyles}
    ${(props) => props.$sender === "userHomeLang" && userMessageStyles}
    ${(props) => props.$sender === "speech" && userMessageStyles}
    ${(props) => props.$sender === "correction" && correctionMessageStyles}
    ${(props) => props.$sender === "caption" && captionMessageStyles}
    ${(props) => props.$sender === "bjorn" && bjornMessageStyles}
    ${(props) => props.$sender === "loader" && loaderMessageStyles}
    ${(props) => props.$sender === "loaderL" && loaderLeftStyles}
    ${(props) => props.$sender === "loaderR" && loaderRightStyles}
    ${(props) => props.$sender === "options" && optionsMessageStyles}
    ${(props) => props.$sender === "optionsSmall" && optionsSmallStyles}
    ${(props) => props.$sender === "grammarWhyButton" && optionsSmallStyles}
    ${(props) => props.$sender === "languageOptions" && fullWidthOptionsStyles}
    ${(props) => props.$sender === "roleplayOptions" && fullWidthOptionsStyles}
    ${(props) => props.$sender === "deckOptions" && fullWidthOptionsStyles}
    ${(props) => props.$sender === "podcastOptions" && fullWidthOptionsStyles}
    ${(props) => props.$sender === "optionsGrid" && fullWidthOptionsStyles}
    ${(props) => props.$sender === "typeMode" && typeModeMessageStyles}
    ${(props) =>
        props.$sender === "multipleChoice" && multipleChoiceMessageStyles}
    ${(props) => props.$sender === "system" && systemMessageStyles}

    /* Apply modifier styles */
    ${(props) => props.$contextSetterMessage && contextSetterStyles}
    ${(props) => props.type === "starting" && startingMessageStyles}

    /* Extra margin after captions */
    ${(props) =>
        props.$followsCaption &&
        props.$sender !== "caption" &&
        `margin-top: 3em;`}

    /* Custom margin-top from message prop */
    ${(props) =>
        props.$marginTop && `margin-top: ${props.$marginTop} !important;`}
`;

export const ExampleWithAudioContainer = styled.div`
    .replay-audio {
        opacity: 1;
        transform: translateY(48%) scale(0.7) !important;
    }

    ${media.mobile} {
        .replay-audio {
            opacity: 1;
        }
    }
`;

export const Message = styled(SharedMessageStyles)``;

export const Avatar = styled.div`
    width: 1.8em;
    height: 1.8em;
    ${borderRadius(0.2)};
    overflow: hidden;
    position: absolute;
    left: -1.2em;
    translate: -100% 0%;
    bottom: 0;
    opacity: 0.7;
    pointer-events: none;
    mask: url("${getCSSImageUrl("img/mask-avatar.svg")}") no-repeat center /
        contain;
    -webkit-mask: url("${getCSSImageUrl("img/mask-avatar.svg")}") no-repeat
        center / contain;
    background: ${getColor("colorAI")};

    ${({ $sender }) =>
        ($sender === "user" || $sender === "userHomeLang") &&
        css`
            background: ${getColor("colorYou")};
            left: auto;
            right: -1.2em;
            translate: 100% 0;
        `}

    > div {
        width: 100%;
        height: auto;
        max-height: 0.8em;
        position: absolute;
        top: 50%;
        left: 50%;
        translate: -50% -50%;
        background: ${getColor("colortextAi")};
        mask: url("${getCSSImageUrl("img/mask-bjo.svg")}") no-repeat center /
            contain;
        -webkit-mask: url("${getCSSImageUrl("img/mask-bjo.svg")}") no-repeat
            center / contain;
        mask-size: auto 90%;
        -webkit-mask-size: auto 90%;

        ${({ $sender }) =>
            ($sender === "user" || $sender === "userHomeLang") &&
            css`
                background: ${getColor("colortextYou")};
                mask: url("${getCSSImageUrl("img/mask-you.svg")}") no-repeat
                    center / contain;
                -webkit-mask: url("${getCSSImageUrl("img/mask-you.svg")}")
                    no-repeat center / contain;
            `}

        img {
            width: 100%;
            height: auto;
            opacity: 0;
        }
    }

    ${media.tablet} {
        display: none;
    }
`;

export const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    color: ${getColor("black", 0.5)};
    text-align: center;
    padding: 2em;
    margin-bottom: 2em;

    p {
        font-size: 1.1em;
        margin-bottom: 0.5em;
    }

    span {
        font-size: 0.9em;
        opacity: 0.7;
    }
`;
